import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Col, Row, Card, CardBody, CardHeader } from "stories/layout";
import { Typography } from "stories/components";
import { thousandFormat } from "services/helpers";
import { useAppContext } from "contexts/AppContext";

const PortfolioSummaryCard = ({ portfoliosSummary }) => {
  const { t } = useTranslation();
  const { getPortfolioName } = useAppContext();
  const [activeIndex, setActiveIndex] = useState(0);

  if (!portfoliosSummary || Object.keys(portfoliosSummary).length === 0) {
    return (
      <Card className="shadow-sm-strong h-100 portfolio-summary-card">
        <CardBody className="d-flex justify-content-center align-items-center">
          <Typography>Loading...</Typography>
        </CardBody>
      </Card>
    );
  }

  // Get portfolio data
  const portfolioIds = Object.keys(portfoliosSummary);
  const currentPortfolioId = portfolioIds[activeIndex];
  const portfolioData = portfoliosSummary[currentPortfolioId];
  const portfolioName = getPortfolioName(Number(currentPortfolioId));

  // Calculate values from current portfolio
  const hankintameno = portfolioData?.Hankintahinta || 0;
  const markkina_arvo = portfolioData?.Markkina_arvo || 0;
  const realisoitumattomat_tuotot = portfolioData?.Realisoitumattomat_tuotot || 0;
  const realisoituneet_tuotot = portfolioData?.Realisoituneet_tuotot || 0;
  
  // Calculate profit percentage
  const profit_percentage = hankintameno > 0 ? ((markkina_arvo - hankintameno) / hankintameno * 100) : 0;
  
  // Navigation functions
  const next = () => {
    const nextIndex = activeIndex === portfolioIds.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    const nextIndex = activeIndex === 0 ? portfolioIds.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  // Determine colors based on profit/loss
  const isProfit = realisoitumattomat_tuotot >= 0;
  const profitColor = isProfit ? 'text-success' : 'text-danger';

  return (
    <Card className="shadow-sm-strong h-100 portfolio-summary-card">
      <CardHeader className="sk-frontpage-card-header portfolio-summary-card__header d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
          <div className="sk-card-header-icon-box mr-2 flex-shrink-0" aria-hidden>
            <i className="fas fa-chart-bar text-muted" />
          </div>
          <Typography variant="h4" className="text-bold mb-0 text-truncate">
            {portfolioName}
          </Typography>
        </div>
        {/* Navigation arrows */}
        {portfolioIds.length > 1 && (
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-sm portfolio-summary-card__nav-btn mr-1"
              onClick={previous}
            >
              <i className="fas fa-chevron-left" />
            </button>
            <span className="mx-2 small text-muted">
              {activeIndex + 1}/{portfolioIds.length}
            </span>
            <button
              type="button"
              className="btn btn-sm portfolio-summary-card__nav-btn"
              onClick={next}
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}
      </CardHeader>
      
      <CardBody className="p-3">
        {/* Main summary section */}
        <Row className="mb-3">
          <Col xs="6">
            <div className="text-left">
              <Typography variant="small" className="text-muted text-uppercase">
                <i className="fas fa-euro-sign text-muted mr-1" />
                {t("acquisition_cost", "Hankintameno")}
              </Typography>
              <Typography variant="h4" className="text-bold text-dark mb-2">
                {thousandFormat(hankintameno.toFixed(2))} €
              </Typography>
            </div>
          </Col>
          <Col xs="6">
            <div className="text-right">
              <Typography variant="small" className="text-muted text-uppercase">
                <i className="fas fa-chart-bar text-primary mr-1" />
                {t("market_value", "Markkina-arvo")}
              </Typography>
              <Typography variant="h4" className="text-bold text-primary mb-2">
                {thousandFormat(markkina_arvo.toFixed(2))} €
              </Typography>
            </div>
          </Col>
        </Row>

        {/* Profit section with percentage */}
        <Row className="mb-3">
          <Col xs="8">
            <Typography variant="small" className="text-muted text-uppercase">
              <i className="fas fa-trending-up text-success mr-1" />
              {t("total_profit", "Kokonaistuotto")}
            </Typography>
            <Typography variant="h3" className={`text-bold ${profitColor}`}>
              {isProfit ? '+' : ''}{thousandFormat(realisoitumattomat_tuotot.toFixed(2))} €
            </Typography>
          </Col>
          <Col xs="4" className="text-right">
            <Typography variant="small" className="text-muted text-uppercase mb-1">
              {t("percentage", "%")}
            </Typography>
            <Typography variant="h3" className={`text-bold ${profitColor}`}>
              {isProfit ? '+' : ''}{profit_percentage.toFixed(1)}%
            </Typography>
          </Col>
        </Row>

        {/* Development indicator */}
        <div className="mb-3">
          <Typography variant="small" className="text-muted">
            {t("development_period", "Sijoitusten kehitys")}
          </Typography>
          <div className="progress-wrapper">
            <div className="progress" style={{ height: '6px', backgroundColor: '#e9ecef' }}>
              <div 
                className={`progress-bar ${isProfit ? 'bg-success' : 'bg-danger'}`}
                style={{ width: `${Math.min(Math.abs(profit_percentage), 100)}%` }}
              />
            </div>
          </div>
        </div>

        {/* Real profits section */}
        <Row>
          <Col>
            <div className="border-top pt-2">
              <Typography variant="small" className="text-muted text-uppercase">
                <i className="fas fa-money-bill text-success mr-1" />
                {t("realized_profits", "Real. tuotot")}
              </Typography>
              <Typography variant="h5" className="text-bold text-success mb-0">
                +{thousandFormat(realisoituneet_tuotot.toFixed(2))} €
              </Typography>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default PortfolioSummaryCard;
